import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companyService } from '../../services/companyService';
import { subscriptionPlanAdminService } from '../../services/subscriptionPlanAdminService';
import toast from 'react-hot-toast';

import { Company, CompanyUser } from '../../types/company';
import { SubscriptionPlan } from '../../types/subscriptionPlan';
import { Badge } from '../../components/ui';

type TabKey = 'overview' | 'users' | 'consumption' | 'transactions' | 'invoices';

const tabLabels: Record<TabKey, string> = {
    overview: 'Visão Geral',
    users: 'Membros',
    consumption: 'Consumo',
    transactions: 'Transações',
    invoices: 'Faturas',
};

interface AdminUser extends CompanyUser {
    consumption30d?: number;
    isOwner?: boolean;
}

const AdminCompanyDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [company, setCompany] = useState<Company | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabKey>('overview');

    // Users state (fetched separately via admin endpoint)
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    // Rejection modal state
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [isRejecting, setIsRejecting] = useState(false);
    const [isApproving, setIsApproving] = useState(false);

    // Add credits modal state
    const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
    const [creditsAmount, setCreditsAmount] = useState('');
    const [creditsDescription, setCreditsDescription] = useState('');
    const [isAddingCredits, setIsAddingCredits] = useState(false);

    // Deactivate modal state
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [isDeactivating, setIsDeactivating] = useState(false);

    // Edit company modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState<Record<string, string>>({});
    const [isSavingEdit, setIsSavingEdit] = useState(false);

    // Edit fiscal modal state
    const [showEditFiscalModal, setShowEditFiscalModal] = useState(false);
    const [fiscalData, setFiscalData] = useState<Record<string, string>>({});
    const [isSavingFiscal, setIsSavingFiscal] = useState(false);

    // Change plan modal state
    const [showChangePlanModal, setShowChangePlanModal] = useState(false);
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [isChangingPlan, setIsChangingPlan] = useState(false);
    const [isLoadingPlans, setIsLoadingPlans] = useState(false);

    // Change role modal state
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [roleTargetUser, setRoleTargetUser] = useState<AdminUser | null>(null);
    const [isChangingRole, setIsChangingRole] = useState(false);

    // Delete user modal state
    const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
    const [deleteTargetUser, setDeleteTargetUser] = useState<AdminUser | null>(null);
    const [isDeletingUser, setIsDeletingUser] = useState(false);

    // User actions dropdown
    const [openDropdownUserId, setOpenDropdownUserId] = useState<string | null>(null);

    // Create user modal state
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [createUserData, setCreateUserData] = useState({ name: '', email: '', role: 'member' });
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [createdUserPassword, setCreatedUserPassword] = useState<string | null>(null);

    // Invite user modal state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [isInviting, setIsInviting] = useState(false);

    // Add existing user modal state
    const [showAddExistingModal, setShowAddExistingModal] = useState(false);
    const [searchEmail, setSearchEmail] = useState('');
    const [searchResult, setSearchResult] = useState<{ id: string; name: string; email: string; companyId?: string } | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [addExistingRole, setAddExistingRole] = useState('member');
    const [isAddingExisting, setIsAddingExisting] = useState(false);

    const fetchCompany = useCallback(async () => {
        if (!id) return;
        try {
            const data = await companyService.adminGet(id);
            setCompany(data);
        } catch (error) {
            console.error('Failed to fetch company', error);
            toast.error('Erro ao carregar detalhes da empresa.');
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    const fetchUsers = useCallback(async () => {
        if (!id) return;
        setIsLoadingUsers(true);
        try {
            const response = await companyService.adminListUsers(id) as { users: AdminUser[] } | AdminUser[];
            const data = Array.isArray(response) ? response : (response as { users: AdminUser[] }).users ?? [];
            setUsers(data);
        } catch (error) {
            console.error('Failed to fetch users', error);
            // Fallback to company.users if admin endpoint fails
            if (company?.users) {
                setUsers(company.users.map(u => ({ ...u, consumption30d: 0 })));
            }
        } finally {
            setIsLoadingUsers(false);
        }
    }, [id, company?.users]);

    useEffect(() => {
        if (id) fetchCompany();
    }, [id, fetchCompany]);

    useEffect(() => {
        if (id && (activeTab === 'users' || activeTab === 'consumption')) {
            fetchUsers();
        }
    }, [id, activeTab, fetchUsers]);

    // --- Handlers ---

    const handleApprove = async () => {
        if (!company) return;
        setIsApproving(true);
        try {
            await companyService.adminApprove(company.id);
            toast.success('Empresa aprovada com sucesso!');
            await fetchCompany();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao aprovar empresa');
        } finally {
            setIsApproving(false);
        }
    };

    const handleReject = async () => {
        if (!company || !rejectReason.trim()) {
            toast.error('Por favor, informe o motivo da rejeição.');
            return;
        }
        setIsRejecting(true);
        try {
            await companyService.adminReject(company.id, rejectReason);
            toast.success('Empresa rejeitada. O usuário poderá corrigir os dados.');
            setShowRejectModal(false);
            setRejectReason('');
            await fetchCompany();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao rejeitar empresa');
        } finally {
            setIsRejecting(false);
        }
    };

    const handleAddCredits = async () => {
        if (!company || !creditsAmount) return;
        const amount = parseInt(creditsAmount, 10);
        if (isNaN(amount) || amount <= 0) {
            toast.error('Informe um valor válido de créditos.');
            return;
        }
        setIsAddingCredits(true);
        try {
            await companyService.adminAddCredits(company.id, amount, creditsDescription || 'Créditos adicionados pelo admin');
            toast.success(`${amount} créditos adicionados com sucesso!`);
            setShowAddCreditsModal(false);
            setCreditsAmount('');
            setCreditsDescription('');
            await fetchCompany();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao adicionar créditos');
        } finally {
            setIsAddingCredits(false);
        }
    };

    const handleDeactivate = async () => {
        if (!company) return;
        setIsDeactivating(true);
        try {
            await companyService.adminDestroy(company.id);
            toast.success('Empresa inativada com sucesso.');
            navigate('/admin/companies');
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao inativar empresa');
        } finally {
            setIsDeactivating(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!company) return;
        setIsSavingEdit(true);
        try {
            await companyService.adminUpdate(company.id, editData);
            toast.success('Dados da empresa atualizados!');
            setShowEditModal(false);
            await fetchCompany();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao atualizar empresa');
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleSaveFiscal = async () => {
        if (!company) return;
        setIsSavingFiscal(true);
        try {
            await companyService.adminUpdate(company.id, fiscalData);
            toast.success('Dados fiscais atualizados!');
            setShowEditFiscalModal(false);
            await fetchCompany();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao atualizar dados fiscais');
        } finally {
            setIsSavingFiscal(false);
        }
    };

    const handleOpenChangePlan = async () => {
        setShowChangePlanModal(true);
        setIsLoadingPlans(true);
        try {
            const allPlans = await subscriptionPlanAdminService.getAll();
            setPlans(allPlans.filter(p => p.isActive));
        } catch {
            toast.error('Erro ao carregar planos.');
        } finally {
            setIsLoadingPlans(false);
        }
    };

    const handleChangePlan = async () => {
        if (!company || !selectedPlanId) return;
        setIsChangingPlan(true);
        try {
            await companyService.adminChangeSubscription(company.id, selectedPlanId);
            toast.success('Plano alterado com sucesso!');
            setShowChangePlanModal(false);
            setSelectedPlanId('');
            await fetchCompany();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao alterar plano');
        } finally {
            setIsChangingPlan(false);
        }
    };

    const handleChangeUserRole = async () => {
        if (!company || !roleTargetUser) return;
        const newRole = roleTargetUser.companyRole === 'admin' ? 'member' : 'admin';
        setIsChangingRole(true);
        try {
            await companyService.adminChangeUserRole(company.id, roleTargetUser.id, newRole);
            toast.success(`Papel alterado para ${newRole}!`);
            setShowRoleModal(false);
            setRoleTargetUser(null);
            await fetchUsers();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao alterar papel');
        } finally {
            setIsChangingRole(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!company || !deleteTargetUser) return;
        setIsDeletingUser(true);
        try {
            await companyService.adminDeleteUser(company.id, deleteTargetUser.id);
            toast.success('Usuário excluído da empresa.');
            setShowDeleteUserModal(false);
            setDeleteTargetUser(null);
            await fetchUsers();
            await fetchCompany();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao excluir usuário');
        } finally {
            setIsDeletingUser(false);
        }
    };

    // --- Create User Handler ---
    const handleCreateUser = async () => {
        if (!company || !createUserData.name || !createUserData.email) return;
        setIsCreatingUser(true);
        try {
            await companyService.adminCreateUser(company.id, createUserData);
            setCreatedUserPassword('sent');
            await fetchUsers();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao criar usuário');
        } finally {
            setIsCreatingUser(false);
        }
    };

    const closeCreateUserModal = () => {
        setShowCreateUserModal(false);
        setCreateUserData({ name: '', email: '', role: 'member' });
        setCreatedUserPassword(null);
    };

    // --- Reset Password Handler ---
    const handleResetPassword = async (userId: string) => {
        if (!company) return;
        const confirmed = window.confirm(
            'A senha atual deste usuário será descontinuada e uma nova senha temporária será enviada por email. Deseja continuar?'
        );
        if (!confirmed) return;
        try {
            await companyService.adminResetPassword(company.id, userId);
            toast.success('Nova senha enviada por email ao usuário.');
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao resetar senha.');
        }
    };

    // --- Invite User Handler ---
    const handleInviteUser = async () => {
        if (!company || !inviteEmail) return;
        setIsInviting(true);
        try {
            await companyService.adminInviteUser(company.id, inviteEmail);
            toast.success('Convite enviado com sucesso.');
            setShowInviteModal(false);
            setInviteEmail('');
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao enviar convite');
        } finally {
            setIsInviting(false);
        }
    };

    // --- Add Existing User Handler ---
    const handleSearchUser = async () => {
        if (!searchEmail) return;
        setIsSearching(true);
        setSearchResult(null);
        try {
            const result = await companyService.adminSearchUsers(searchEmail);
            setSearchResult(result);
        } catch {
            toast.error('Usuário não encontrado.');
        } finally {
            setIsSearching(false);
        }
    };

    const handleAddExistingUser = async () => {
        if (!company || !searchResult) return;
        setIsAddingExisting(true);
        try {
            await companyService.adminAddExistingUser(company.id, searchResult.id, addExistingRole);
            toast.success('Usuário vinculado com sucesso.');
            setShowAddExistingModal(false);
            setSearchEmail('');
            setSearchResult(null);
            await fetchUsers();
            await fetchCompany();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            toast.error(err.response?.data?.message || 'Erro ao vincular usuário');
        } finally {
            setIsAddingExisting(false);
        }
    };

    const openEditModal = () => {
        if (!company) return;
        setEditData({
            name: company.name || '',
            billing_email: company.billingEmail || '',
            phone: company.phone || '',
        });
        setShowEditModal(true);
    };

    const openEditFiscalModal = () => {
        if (!company) return;
        setFiscalData({
            document: company.document || '',
            address: company.address || '',
            address_number: company.addressNumber || '',
            address_complement: company.addressComplement || '',
            neighborhood: company.neighborhood || '',
            city: company.city || '',
            state: company.state || '',
            postal_code: company.postalCode || '',
        });
        setShowEditFiscalModal(true);
    };

    // --- Helpers ---

    const isOwner = (user: AdminUser) => {
        return user.isOwner || user.companyRole === 'owner' || String(user.id) === String(company?.ownerId);
    };

    const getRoleBadge = (user: AdminUser) => {
        if (isOwner(user)) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                    <span>&#128081;</span> Owner
                </span>
            );
        }
        if (user.companyRole === 'admin') {
            return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-snapfy-primary/20 text-snapfy-accent-soft">Admin</span>;
        }
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-snapfy-surface-hover text-snapfy-text-secondary">Membro</span>;
    };

    // --- Render helpers ---

    if (isLoading) return <div className="p-8 text-snapfy-text-primary">Carregando...</div>;
    if (!company) return <div className="p-8 text-snapfy-text-primary">Empresa não encontrada.</div>;

    const statusColors: Record<string, string> = {
        approved: 'bg-green-500/20 text-green-400',
        pending: 'bg-yellow-500/20 text-yellow-400',
        rejected: 'bg-red-500/20 text-red-400',
        inactive: 'bg-snapfy-surface-hover text-snapfy-text-secondary',
    };

    const statusLabels: Record<string, string> = {
        approved: 'Aprovada',
        pending: 'Pendente',
        rejected: 'Rejeitada',
        inactive: 'Inativa',
    };

    // Consumption summary from users
    const consumptionSummary = users.reduce(
        (acc, u) => {
            acc.totalCredits += u.consumption30d || 0;
            return acc;
        },
        { totalCredits: 0 }
    );

    // Find owner name
    const ownerUser = company.users?.find(u => String(u.id) === String(company.ownerId)) || users.find(u => isOwner(u));

    return (
        <div className="container mx-auto px-4 py-8 text-snapfy-text-primary">
            {/* ========== MODALS ========== */}

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4">Rejeitar Empresa</h3>
                        <p className="text-snapfy-text-secondary mb-4">
                            Informe o motivo da rejeição. O usuário receberá esta informação e poderá corrigir os dados.
                        </p>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Ex: CNPJ inválido, endereço incompleto, razão social não confere..."
                            className="w-full h-32 bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary resize-none"
                        />
                        <div className="flex justify-end space-x-3 mt-4">
                            <button
                                onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                                className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={isRejecting || !rejectReason.trim()}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                            >
                                {isRejecting ? 'Rejeitando...' : 'Confirmar Rejeição'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Credits Modal */}
            {showAddCreditsModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4">Adicionar Créditos</h3>
                        <p className="text-snapfy-text-secondary mb-4">
                            Informe a quantidade de créditos e uma descrição para o registro.
                        </p>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-snapfy-text-secondary mb-1">Quantidade de créditos</label>
                                <input
                                    type="number"
                                    value={creditsAmount}
                                    onChange={(e) => setCreditsAmount(e.target.value)}
                                    placeholder="Ex: 500"
                                    min="1"
                                    className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-snapfy-text-secondary mb-1">Descrição (opcional)</label>
                                <input
                                    type="text"
                                    value={creditsDescription}
                                    onChange={(e) => setCreditsDescription(e.target.value)}
                                    placeholder="Ex: Bonificação, ajuste manual..."
                                    className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => { setShowAddCreditsModal(false); setCreditsAmount(''); setCreditsDescription(''); }}
                                className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleAddCredits}
                                disabled={isAddingCredits || !creditsAmount}
                                className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md disabled:opacity-50"
                            >
                                {isAddingCredits ? 'Adicionando...' : 'Adicionar Créditos'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Deactivate Modal */}
            {showDeactivateModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4 text-red-400">Inativar Empresa</h3>
                        <p className="text-snapfy-text-secondary mb-4">
                            Tem certeza que deseja inativar <strong className="text-snapfy-text-primary">{company.name}</strong>? Esta ação pode ser irreversível.
                        </p>
                        <div className="flex justify-end space-x-3 mt-4">
                            <button
                                onClick={() => setShowDeactivateModal(false)}
                                className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeactivate}
                                disabled={isDeactivating}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                            >
                                {isDeactivating ? 'Inativando...' : 'Confirmar Inativação'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Company Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-lg">
                        <h3 className="text-xl font-bold mb-4">Editar Dados da Organização</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-snapfy-text-secondary mb-1">Nome</label>
                                <input
                                    type="text"
                                    value={editData.name || ''}
                                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                    className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-snapfy-text-secondary mb-1">Email Financeiro</label>
                                <input
                                    type="email"
                                    value={editData.billing_email || ''}
                                    onChange={(e) => setEditData({ ...editData, billing_email: e.target.value })}
                                    className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-snapfy-text-secondary mb-1">Telefone</label>
                                <input
                                    type="text"
                                    value={editData.phone || ''}
                                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                                    className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button onClick={() => setShowEditModal(false)} className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary">
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                disabled={isSavingEdit}
                                className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md disabled:opacity-50"
                            >
                                {isSavingEdit ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Fiscal Modal */}
            {showEditFiscalModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <h3 className="text-xl font-bold mb-4">Editar Dados Fiscais</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-snapfy-text-secondary mb-1">CNPJ / CPF</label>
                                <input
                                    type="text"
                                    value={fiscalData.document || ''}
                                    onChange={(e) => setFiscalData({ ...fiscalData, document: e.target.value })}
                                    className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-snapfy-text-secondary mb-1">Endereço</label>
                                    <input
                                        type="text"
                                        value={fiscalData.address || ''}
                                        onChange={(e) => setFiscalData({ ...fiscalData, address: e.target.value })}
                                        className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-snapfy-text-secondary mb-1">Número</label>
                                    <input
                                        type="text"
                                        value={fiscalData.address_number || ''}
                                        onChange={(e) => setFiscalData({ ...fiscalData, address_number: e.target.value })}
                                        className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-snapfy-text-secondary mb-1">Complemento</label>
                                <input
                                    type="text"
                                    value={fiscalData.address_complement || ''}
                                    onChange={(e) => setFiscalData({ ...fiscalData, address_complement: e.target.value })}
                                    className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-snapfy-text-secondary mb-1">Bairro</label>
                                    <input
                                        type="text"
                                        value={fiscalData.neighborhood || ''}
                                        onChange={(e) => setFiscalData({ ...fiscalData, neighborhood: e.target.value })}
                                        className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-snapfy-text-secondary mb-1">Cidade</label>
                                    <input
                                        type="text"
                                        value={fiscalData.city || ''}
                                        onChange={(e) => setFiscalData({ ...fiscalData, city: e.target.value })}
                                        className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-snapfy-text-secondary mb-1">Estado</label>
                                    <input
                                        type="text"
                                        value={fiscalData.state || ''}
                                        onChange={(e) => setFiscalData({ ...fiscalData, state: e.target.value })}
                                        className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-snapfy-text-secondary mb-1">CEP</label>
                                    <input
                                        type="text"
                                        value={fiscalData.postal_code || ''}
                                        onChange={(e) => setFiscalData({ ...fiscalData, postal_code: e.target.value })}
                                        className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md p-3 text-snapfy-text-primary focus:ring-snapfy-focus focus:border-snapfy-primary"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button onClick={() => setShowEditFiscalModal(false)} className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary">
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveFiscal}
                                disabled={isSavingFiscal}
                                className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md disabled:opacity-50"
                            >
                                {isSavingFiscal ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Plan Modal */}
            {showChangePlanModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4">Alterar Plano</h3>
                        {isLoadingPlans ? (
                            <p className="text-snapfy-text-secondary">Carregando planos...</p>
                        ) : (
                            <div className="space-y-3">
                                {plans.map((plan) => (
                                    <label
                                        key={plan.id}
                                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                            selectedPlanId === String(plan.id)
                                                ? 'border-snapfy-primary bg-snapfy-primary/10'
                                                : 'border-snapfy-border bg-snapfy-surface-hover hover:border-snapfy-border'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="radio"
                                                name="plan"
                                                value={String(plan.id)}
                                                checked={selectedPlanId === String(plan.id)}
                                                onChange={(e) => setSelectedPlanId(e.target.value)}
                                                className="accent-snapfy-primary"
                                            />
                                            <div>
                                                <p className="font-medium">{plan.name}</p>
                                                <p className="text-sm text-snapfy-text-secondary">{plan.totalCredits} créditos</p>
                                            </div>
                                        </div>
                                        <span className="text-snapfy-accent-soft font-medium">R$ {plan.price}</span>
                                    </label>
                                ))}
                                {plans.length === 0 && (
                                    <p className="text-snapfy-text-secondary text-center py-4">Nenhum plano ativo encontrado.</p>
                                )}
                            </div>
                        )}
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => { setShowChangePlanModal(false); setSelectedPlanId(''); }}
                                className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleChangePlan}
                                disabled={isChangingPlan || !selectedPlanId}
                                className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md disabled:opacity-50"
                            >
                                {isChangingPlan ? 'Alterando...' : 'Alterar Plano'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Role Modal */}
            {showRoleModal && roleTargetUser && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4">Alterar Papel do Usuário</h3>
                        <p className="text-snapfy-text-secondary mb-4">
                            {roleTargetUser.companyRole === 'admin'
                                ? `Rebaixar ${roleTargetUser.name} de Admin para Membro?`
                                : `Promover ${roleTargetUser.name} de Membro para Admin?`}
                        </p>
                        <div className="flex justify-end space-x-3 mt-4">
                            <button
                                onClick={() => { setShowRoleModal(false); setRoleTargetUser(null); }}
                                className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleChangeUserRole}
                                disabled={isChangingRole}
                                className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md disabled:opacity-50"
                            >
                                {isChangingRole ? 'Alterando...' : 'Confirmar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete User Modal */}
            {showDeleteUserModal && deleteTargetUser && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4 text-red-400">Excluir Usuário</h3>
                        <p className="text-snapfy-text-secondary mb-4">
                            Tem certeza que deseja excluir <strong className="text-snapfy-text-primary">{deleteTargetUser.name}</strong> ({deleteTargetUser.email}) desta empresa?
                        </p>
                        <div className="flex justify-end space-x-3 mt-4">
                            <button
                                onClick={() => { setShowDeleteUserModal(false); setDeleteTargetUser(null); }}
                                className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeleteUser}
                                disabled={isDeletingUser}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                            >
                                {isDeletingUser ? 'Excluindo...' : 'Confirmar Exclusão'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create User Modal */}
            {showCreateUserModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-md">
                        {createdUserPassword ? (
                            <div className="text-center">
                                <svg className="w-12 h-12 text-green-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h3 className="text-xl font-bold mb-2 text-green-400">Usuário Criado!</h3>
                                <p className="text-snapfy-text-secondary mb-1">Uma senha temporária foi enviada para:</p>
                                <p className="text-snapfy-text-primary font-medium mb-4">{createUserData.email}</p>
                                <p className="text-snapfy-text-secondary text-sm mb-6">O usuário poderá alterar a senha na aba Segurança do perfil após o primeiro login.</p>
                                <button onClick={closeCreateUserModal} className="w-full px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md transition-colors">Fechar</button>
                            </div>
                        ) : (
                            <>
                                <h3 className="text-xl font-bold mb-4">Criar Usuário</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm text-snapfy-text-secondary mb-1">Nome *</label>
                                        <input value={createUserData.name} onChange={e => setCreateUserData(d => ({ ...d, name: e.target.value }))} className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md px-3 py-2 text-snapfy-text-primary" />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-snapfy-text-secondary mb-1">Email *</label>
                                        <input value={createUserData.email} onChange={e => setCreateUserData(d => ({ ...d, email: e.target.value }))} type="email" className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md px-3 py-2 text-snapfy-text-primary" />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-snapfy-text-secondary mb-1">Papel</label>
                                        <select value={createUserData.role} onChange={e => setCreateUserData(d => ({ ...d, role: e.target.value }))} className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md px-3 py-2 text-snapfy-text-primary">
                                            <option value="member">Membro</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex justify-end space-x-3 mt-6">
                                    <button onClick={closeCreateUserModal} className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary">Cancelar</button>
                                    <button onClick={handleCreateUser} disabled={isCreatingUser || !createUserData.name || !createUserData.email} className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md disabled:opacity-50">
                                        {isCreatingUser ? 'Criando...' : 'Criar'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Invite User Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4">Convidar por Email</h3>
                        <div>
                            <label className="block text-sm text-snapfy-text-secondary mb-1">Email *</label>
                            <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} type="email" placeholder="email@exemplo.com" className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-md px-3 py-2 text-snapfy-text-primary" />
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button onClick={() => { setShowInviteModal(false); setInviteEmail(''); }} className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary">Cancelar</button>
                            <button onClick={handleInviteUser} disabled={isInviting || !inviteEmail} className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md disabled:opacity-50">
                                {isInviting ? 'Enviando...' : 'Enviar Convite'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Existing User Modal */}
            {showAddExistingModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4">Vincular Usuário Existente</h3>
                        <div className="flex gap-2 mb-4">
                            <input value={searchEmail} onChange={e => setSearchEmail(e.target.value)} type="email" placeholder="Buscar por email..." className="flex-1 bg-snapfy-surface-hover border border-snapfy-border rounded-md px-3 py-2 text-snapfy-text-primary" />
                            <button onClick={handleSearchUser} disabled={isSearching || !searchEmail} className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md disabled:opacity-50">
                                {isSearching ? '...' : 'Buscar'}
                            </button>
                        </div>
                        {searchResult && (
                            <div className="bg-snapfy-surface-hover rounded p-3 border border-snapfy-border mb-4">
                                <p className="text-snapfy-text-primary font-medium">{searchResult.name}</p>
                                <p className="text-snapfy-text-secondary text-sm">{searchResult.email}</p>
                                {searchResult.companyId ? (
                                    <p className="text-red-400 text-sm mt-1">Já pertence a uma empresa</p>
                                ) : (
                                    <div className="mt-3">
                                        <label className="block text-sm text-snapfy-text-secondary mb-1">Papel</label>
                                        <select value={addExistingRole} onChange={e => setAddExistingRole(e.target.value)} className="w-full bg-snapfy-surface border border-snapfy-border rounded-md px-3 py-2 text-snapfy-text-primary">
                                            <option value="member">Membro</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex justify-end space-x-3">
                            <button onClick={() => { setShowAddExistingModal(false); setSearchEmail(''); setSearchResult(null); }} className="px-4 py-2 text-snapfy-text-secondary hover:text-snapfy-text-primary">Cancelar</button>
                            {searchResult && !searchResult.companyId && (
                                <button onClick={handleAddExistingUser} disabled={isAddingExisting} className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md disabled:opacity-50">
                                    {isAddingExisting ? 'Vinculando...' : 'Vincular'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ========== HEADER ========== */}

            <div className="flex justify-between items-center mb-6">
                <button onClick={() => navigate('/admin/companies')} className="text-snapfy-text-secondary hover:text-snapfy-text-primary flex items-center gap-2">
                    <span>&larr;</span> <span>Voltar</span>
                </button>
            </div>

            <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6 mb-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-2xl md:text-3xl font-bold break-words">{company.name}</h1>
                            <Badge variant={
                                company.status === 'approved' ? 'success' :
                                company.status === 'pending' ? 'warning' :
                                company.status === 'rejected' ? 'error' : 'info'
                            }>
                                {statusLabels[company.status] || company.status}
                            </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-snapfy-text-secondary text-sm">
                            {company.billingEmail && <span>{company.billingEmail}</span>}
                            {company.activeSubscription?.plan?.name && (
                                <>
                                    <span>•</span>
                                    <span className="text-snapfy-accent-soft">{company.activeSubscription.plan.name}</span>
                                </>
                            )}
                            <span>•</span>
                            <span className="text-snapfy-accent-soft font-medium">{company.creditsBalance?.toLocaleString()} créditos</span>
                        </div>
                        {company.status === 'rejected' && company.rejectionReason && (
                            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <p className="text-sm text-red-400 font-medium">Motivo da Rejeição:</p>
                                <p className="text-red-300">{company.rejectionReason}</p>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {company.status === 'pending' && (
                            <>
                                <button
                                    onClick={() => setShowRejectModal(true)}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-sm font-medium transition-colors"
                                >
                                    Rejeitar
                                </button>
                                <button
                                    onClick={handleApprove}
                                    disabled={isApproving}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-md text-sm font-medium disabled:opacity-50 transition-colors"
                                >
                                    {isApproving ? 'Aprovando...' : 'Aprovar'}
                                </button>
                            </>
                        )}
                        {company.status === 'approved' && (
                            <>
                                <button
                                    onClick={() => setShowAddCreditsModal(true)}
                                    className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md text-sm font-medium transition-colors"
                                >
                                    + Adicionar Créditos
                                </button>
                                <button
                                    onClick={() => setShowDeactivateModal(true)}
                                    className="px-4 py-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-600/30 rounded-md text-sm font-medium transition-colors"
                                >
                                    Inativar Empresa
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ========== TABS ========== */}

            <div className="flex border-b border-snapfy-border mb-6 overflow-x-auto">
                {(Object.keys(tabLabels) as TabKey[]).map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`py-2 px-4 font-medium whitespace-nowrap transition-colors ${
                            activeTab === tab
                                ? 'border-b-2 border-snapfy-primary text-snapfy-primary'
                                : 'text-snapfy-text-secondary hover:text-snapfy-text-primary'
                        }`}
                    >
                        {tabLabels[tab]}
                    </button>
                ))}
            </div>

            {/* ========== TAB CONTENT ========== */}

            {/* Tab: Visão Geral */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Dados da Organização */}
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold">Dados da Organização</h2>
                            <button
                                onClick={openEditModal}
                                className="px-3 py-1.5 text-sm bg-snapfy-surface-hover hover:bg-snapfy-surface-hover border border-snapfy-border rounded-md transition-colors"
                            >
                                Editar
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <p className="text-sm text-snapfy-text-secondary">Nome</p>
                                <p className="font-medium">{company.name}</p>
                            </div>
                            <div>
                                <p className="text-sm text-snapfy-text-secondary">Email Financeiro</p>
                                <p className="font-medium">{company.billingEmail || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-snapfy-text-secondary">Telefone</p>
                                <p className="font-medium">{company.phone || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-snapfy-text-secondary">Proprietário</p>
                                <p className="font-medium">{ownerUser?.name || `ID: ${company.ownerId}`}</p>
                            </div>
                            <div>
                                <p className="text-sm text-snapfy-text-secondary">Criada em</p>
                                <p className="font-medium">{new Date(company.createdAt).toLocaleDateString('pt-BR')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Dados Fiscais */}
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold">Dados Fiscais (NF)</h2>
                            <button
                                onClick={openEditFiscalModal}
                                className="px-3 py-1.5 text-sm bg-snapfy-surface-hover hover:bg-snapfy-surface-hover border border-snapfy-border rounded-md transition-colors"
                            >
                                Editar
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <p className="text-sm text-snapfy-text-secondary">CNPJ / CPF</p>
                                <p className="font-medium">{company.document || 'N/A'}</p>
                            </div>
                            <div className="md:col-span-2">
                                <p className="text-sm text-snapfy-text-secondary">Endereço</p>
                                <p className="font-medium">
                                    {company.address
                                        ? `${company.address}, ${company.addressNumber || 'S/N'} ${company.addressComplement || ''}`
                                        : 'N/A'}
                                </p>
                                {company.neighborhood && (
                                    <p className="text-snapfy-text-secondary text-sm">
                                        {company.neighborhood} - {company.city}/{company.state} - {company.postalCode}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Plano Atual */}
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold">Plano Atual</h2>
                            <button
                                onClick={handleOpenChangePlan}
                                className="px-3 py-1.5 text-sm bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md transition-colors"
                            >
                                Alterar Plano
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <p className="text-sm text-snapfy-text-secondary">Plano</p>
                                <p className="font-medium text-snapfy-accent-soft">
                                    {company.activeSubscription?.plan?.name || 'Nenhum plano ativo'}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-snapfy-text-secondary">Preço</p>
                                <p className="font-medium">
                                    {company.activeSubscription?.plan?.price
                                        ? `R$ ${company.activeSubscription.plan.price}`
                                        : 'N/A'}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-snapfy-text-secondary">Saldo de Créditos</p>
                                <p className="text-2xl font-bold text-snapfy-accent-soft">{company.creditsBalance?.toLocaleString()}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab: Membros */}
            {activeTab === 'users' && (
                <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                        <h2 className="text-lg font-semibold">Membros ({users.length})</h2>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => setShowCreateUserModal(true)} className="px-3 py-1.5 text-sm bg-snapfy-primary hover:bg-[#5a4dd4] rounded-md transition-colors">
                                + Criar Usuário
                            </button>
                            <button onClick={() => setShowInviteModal(true)} className="px-3 py-1.5 text-sm bg-snapfy-surface-hover hover:bg-snapfy-surface-hover border border-snapfy-border rounded-md transition-colors">
                                + Convidar por Email
                            </button>
                            <button onClick={() => setShowAddExistingModal(true)} className="px-3 py-1.5 text-sm bg-snapfy-surface-hover hover:bg-snapfy-surface-hover border border-snapfy-border rounded-md transition-colors">
                                + Vincular Existente
                            </button>
                        </div>
                    </div>

                    {isLoadingUsers ? (
                        <div className="text-center py-8 text-snapfy-text-secondary">Carregando membros...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-left">
                                <thead>
                                    <tr className="text-snapfy-text-secondary border-b border-snapfy-border text-sm">
                                        <th className="py-3 pr-4">Nome</th>
                                        <th className="py-3 pr-4">Email</th>
                                        <th className="py-3 pr-4">Papel</th>
                                        <th className="py-3 pr-4 text-right">Consumo 30d</th>
                                        <th className="py-3 text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((u) => (
                                        <tr key={u.id} className="border-b border-snapfy-border/50 hover:bg-snapfy-surface-hover/30 transition-colors">
                                            <td className="py-3 pr-4 font-medium">{u.name}</td>
                                            <td className="py-3 pr-4 text-snapfy-text-secondary">{u.email}</td>
                                            <td className="py-3 pr-4">{getRoleBadge(u)}</td>
                                            <td className="py-3 pr-4 text-right text-snapfy-text-secondary">
                                                {u.consumption30d !== undefined ? u.consumption30d.toLocaleString() : '-'}
                                            </td>
                                            <td className="py-3 text-right relative">
                                                {!isOwner(u) && (
                                                    <div className="inline-block">
                                                        <button
                                                            onClick={() => setOpenDropdownUserId(openDropdownUserId === u.id ? null : u.id)}
                                                            className="p-1.5 hover:bg-snapfy-surface-hover rounded-md transition-colors text-snapfy-text-secondary hover:text-snapfy-text-primary"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                            </svg>
                                                        </button>
                                                        {openDropdownUserId === u.id && (
                                                            <div className="absolute right-0 mt-1 w-52 bg-snapfy-surface-hover border border-snapfy-border rounded-lg shadow-xl z-10">
                                                                <button
                                                                    onClick={() => {
                                                                        setOpenDropdownUserId(null);
                                                                        setRoleTargetUser(u);
                                                                        setShowRoleModal(true);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-snapfy-surface-hover rounded-t-lg transition-colors"
                                                                >
                                                                    {u.companyRole === 'admin' ? 'Rebaixar a Membro' : 'Promover a Admin'}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setOpenDropdownUserId(null);
                                                                        handleResetPassword(u.id);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2.5 text-sm text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                                                                >
                                                                    Gerar Nova Senha
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setOpenDropdownUserId(null);
                                                                        setDeleteTargetUser(u);
                                                                        setShowDeleteUserModal(true);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 rounded-b-lg transition-colors"
                                                                >
                                                                    Excluir Usuário
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {users.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="text-center py-8 text-snapfy-text-secondary">
                                                Nenhum membro encontrado.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Tab: Consumo */}
            {activeTab === 'consumption' && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-4">
                            <p className="text-sm text-snapfy-text-secondary mb-1">Total Créditos (30d)</p>
                            <p className="text-2xl font-bold text-snapfy-accent-soft">{consumptionSummary.totalCredits.toLocaleString()}</p>
                        </div>
                        <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-4">
                            <p className="text-sm text-snapfy-text-secondary mb-1">Membros</p>
                            <p className="text-2xl font-bold">{users.length}</p>
                        </div>
                        <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-4">
                            <p className="text-sm text-snapfy-text-secondary mb-1">Saldo Atual</p>
                            <p className="text-2xl font-bold text-green-400">{company.creditsBalance?.toLocaleString()}</p>
                        </div>
                        <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-4">
                            <p className="text-sm text-snapfy-text-secondary mb-1">Plano</p>
                            <p className="text-2xl font-bold text-snapfy-accent-soft">{company.activeSubscription?.plan?.name || 'N/A'}</p>
                        </div>
                    </div>

                    {/* Consumption per Member Table */}
                    <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6">
                        <h2 className="text-lg font-semibold mb-4">Consumo por Membro (últimos 30 dias)</h2>
                        {isLoadingUsers ? (
                            <div className="text-center py-8 text-snapfy-text-secondary">Carregando dados de consumo...</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-left">
                                    <thead>
                                        <tr className="text-snapfy-text-secondary border-b border-snapfy-border text-sm">
                                            <th className="py-3 pr-4">Nome</th>
                                            <th className="py-3 pr-4">Email</th>
                                            <th className="py-3 pr-4">Papel</th>
                                            <th className="py-3 text-right">Total Créditos</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((u) => (
                                            <tr key={u.id} className="border-b border-snapfy-border/50 hover:bg-snapfy-surface-hover/30 transition-colors">
                                                <td className="py-3 pr-4 font-medium">{u.name}</td>
                                                <td className="py-3 pr-4 text-snapfy-text-secondary">{u.email}</td>
                                                <td className="py-3 pr-4">{getRoleBadge(u)}</td>
                                                <td className="py-3 text-right font-medium">
                                                    {u.consumption30d !== undefined ? u.consumption30d.toLocaleString() : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                        {users.length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="text-center py-8 text-snapfy-text-secondary">
                                                    Nenhum dado de consumo encontrado.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Tab: Transações */}
            {activeTab === 'transactions' && (
                <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6">
                    <h2 className="text-lg font-semibold mb-4">Transações de Créditos</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left">
                            <thead>
                                <tr className="text-snapfy-text-secondary border-b border-snapfy-border text-sm">
                                    <th className="py-3 pr-4">Data</th>
                                    <th className="py-3 pr-4">Tipo</th>
                                    <th className="py-3 pr-4">Usuário</th>
                                    <th className="py-3 pr-4 text-right">Valor</th>
                                    <th className="py-3">Descrição</th>
                                </tr>
                            </thead>
                            <tbody>
                                {company.creditTransactions?.map((t: Record<string, unknown>) => {
                                    // Try to find user name from users list
                                    const txUserId = t.userId || t.user_id;
                                    const txUser = txUserId
                                        ? (users.find(u => String(u.id) === String(txUserId)) ||
                                           company.users?.find(u => String(u.id) === String(txUserId)))
                                        : null;
                                    return (
                                        <tr key={String(t.id)} className="border-b border-snapfy-border/50 hover:bg-snapfy-surface-hover/30 transition-colors">
                                            <td className="py-3 pr-4 text-snapfy-text-secondary">
                                                {new Date(String(t.createdAt || t.created_at)).toLocaleDateString('pt-BR')}
                                            </td>
                                            <td className="py-3 pr-4">
                                                <Badge variant={
                                                    t.type === 'credit' || t.type === 'grant' ? 'success' : 'error'
                                                }>
                                                    {String(t.type)}
                                                </Badge>
                                            </td>
                                            <td className="py-3 pr-4 text-snapfy-text-secondary">
                                                {txUser ? txUser.name : (txUserId ? `ID: ${txUserId}` : '-')}
                                            </td>
                                            <td className={`py-3 pr-4 text-right font-medium ${
                                                Number(t.amount) < 0 ? 'text-red-400' : 'text-green-400'
                                            }`}>
                                                {Number(t.amount) > 0 ? '+' : ''}{Number(t.amount).toLocaleString()}
                                            </td>
                                            <td className="py-3 text-sm text-snapfy-text-secondary">{String(t.description || '')}</td>
                                        </tr>
                                    );
                                })}
                                {(!company.creditTransactions || company.creditTransactions.length === 0) && (
                                    <tr>
                                        <td colSpan={5} className="text-center py-8 text-snapfy-text-secondary">
                                            Nenhuma transação encontrada.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Tab: Faturas */}
            {activeTab === 'invoices' && (
                <div className="bg-snapfy-surface rounded-xl border border-snapfy-border p-6">
                    <h2 className="text-lg font-semibold mb-4">Faturas</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left">
                            <thead>
                                <tr className="text-snapfy-text-secondary border-b border-snapfy-border text-sm">
                                    <th className="py-3 pr-4">ID</th>
                                    <th className="py-3 pr-4">Valor</th>
                                    <th className="py-3 pr-4">Status</th>
                                    <th className="py-3">Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {company.invoices?.map((inv: Record<string, unknown>) => (
                                    <tr key={String(inv.id)} className="border-b border-snapfy-border/50 hover:bg-snapfy-surface-hover/30 transition-colors">
                                        <td className="py-3 pr-4">{String(inv.id)}</td>
                                        <td className="py-3 pr-4">{String(inv.amount)}</td>
                                        <td className="py-3 pr-4">
                                            <Badge variant={
                                                inv.status === 'paid' ? 'success' :
                                                inv.status === 'pending' ? 'warning' : 'info'
                                            }>
                                                {String(inv.status)}
                                            </Badge>
                                        </td>
                                        <td className="py-3">{new Date(String(inv.createdAt || inv.created_at)).toLocaleDateString('pt-BR')}</td>
                                    </tr>
                                ))}
                                {(!company.invoices || company.invoices.length === 0) && (
                                    <tr>
                                        <td colSpan={4} className="text-center py-8 text-snapfy-text-secondary">
                                            Nenhuma fatura encontrada.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCompanyDetailPage;
